import { ReactNode, useState, useCallback, useEffect } from 'react';

import { version } from '../../../../package.json';

import { PRODUCTION_ENVIRONMENTS } from './CacheBuster.constants';
import { useVersionCheck } from './hooks/useVersionCheck';
import { refreshCacheAndReload } from './utilities/refreshCacheAndReload';

import type { CacheBusterProperties } from './CacheBuster.types';

export const CacheBuster = ({
  children,
  currentVersion = version,
  loadingComponent,
  metaFileDirectory = '.',
  checkInterval = 5 * 60 * 1000,
}: CacheBusterProperties): ReactNode => {
  const [cacheStatus, setCacheStatus] = useState({
    loading: true,
    isLatestVersion: false,
  });

  const isEnabled = PRODUCTION_ENVIRONMENTS.includes(import.meta.env.MODE);
  const debug = import.meta.env.MODE === 'staging';

  const handleNewVersionFound = useCallback(() => {
    setCacheStatus({
      loading: false,
      isLatestVersion: false,
    });
  }, []);

  const handleStatusChange = useCallback((isLatestVersion: boolean) => {
    setCacheStatus({
      loading: false,
      isLatestVersion,
    });
  }, []);

  useVersionCheck({
    enabled: isEnabled,
    metaFileDirectory,
    currentVersion,
    checkInterval,
    onNewVersionFound: handleNewVersionFound,
    onStatusChange: handleStatusChange,
    debug,
  });

  const needsRefresh = !cacheStatus.loading && !cacheStatus.isLatestVersion;

  useEffect(() => {
    if (isEnabled && needsRefresh) {
      refreshCacheAndReload().catch(console.error);
    }
  }, [isEnabled, needsRefresh]);

  if (!isEnabled) {
    return children;
  }

  if (cacheStatus.loading || needsRefresh) {
    return loadingComponent;
  }

  return children;
};
